import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  safePolygon,
  shift,
  size,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles
} from '@floating-ui/react'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import {
  type PropsWithChildren,
  type ReactElement,
  useLayoutEffect
} from 'react'
import { themeClass } from '~/styles/theme.css'
import { closeTrigger, dropdown } from './Downdown.css'

interface DropdownProps extends PropsWithChildren {
  isOpen: boolean
  dropdownRef?: React.RefObject<HTMLElement>
  className?: string
  showCloseTrigger?: boolean
  onclose: () => void
  /** Called when a hover on `dropdownRef` should open the dropdown (see `hoverDelay`). */
  onopen?: () => void
  /** Open on hover of `dropdownRef` after `open` ms and close `close` ms after leaving it or the panel. */
  hoverDelay?: { open: number; close: number }
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  offsetValue?: number
}

export const Dropdown = ({
  children,
  dropdownRef,
  className,
  isOpen,
  showCloseTrigger,
  onclose,
  onopen,
  hoverDelay,
  placement = 'bottom-start',
  offsetValue = 5
}: DropdownProps): ReactElement => {
  const { refs, floatingStyles, context } = useFloating({
    placement,
    open: isOpen,
    onOpenChange: (open) => {
      if (open) {
        onopen?.()
      } else {
        onclose()
      }
    },
    middleware: [
      offset(offsetValue),
      flip(),
      shift(),
      size({
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  // The hover interaction listens on the reference element, which must be known while closed
  useLayoutEffect(() => {
    if (dropdownRef?.current) {
      refs.setReference(dropdownRef.current)
    }
  }, [dropdownRef, refs])

  const hover = useHover(context, {
    enabled: hoverDelay !== undefined,
    delay: hoverDelay,
    handleClose: safePolygon()
  })
  const dismiss = useDismiss(context, {
    outsidePress: (event) => {
      if (dropdownRef?.current?.contains(event.target as Node)) {
        return false
      }
      return true
    }
  })
  const role = useRole(context, { role: 'menu' })
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 150,
    initial: {
      opacity: 0,
      transform: 'translateY(-10px)'
    },
    open: {
      opacity: 1,
      transform: 'translateY(0px)'
    },
    close: {
      opacity: 0,
      transform: 'translateY(-10px)'
    }
  })

  const { getFloatingProps } = useInteractions([hover, dismiss, role])

  return (
    <>
      {isMounted && (
        <FloatingPortal>
          <div
            onClick={(e) => {
              e.stopPropagation()
            }}
            ref={(node) => {
              refs.setFloating(node)
              if (dropdownRef?.current) {
                refs.setReference(dropdownRef.current)
              }
            }}
            style={{
              ...floatingStyles,
              zIndex: 1000
            }}
            {...getFloatingProps()}
          >
            <div
              style={transitionStyles}
              className={clsx(themeClass, dropdown, className)}
            >
              {showCloseTrigger && (
                <span onClick={onclose} className={closeTrigger}>
                  <FontAwesomeIcon icon={faTimes} />
                </span>
              )}
              {children}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
